import React, { useEffect, useState } from "react";
import axios from "axios";
import { Checkbox, Switch } from "@mui/material";
import { toast } from "react-toastify";
import Hoc from "../../component/HOC";
import { devapi } from "../../constants/api";
import {
  getSiteSettingsApi,
  updateSiteSettingsApi,
  getFreePlanFeaturesApi,
  updateFreePlanFeaturesApi,
} from "../../redux/action/user";
import "../ProgramSubscriptions/Program.scss";

const defaultIntro = () => ({
  enabled: true,
  title: "🚨BEFORE YOU BEGIN🚨",
  bullets: [
    "No one will see your responses without your express permission.",
    "Honesty unlocks your best results.",
    "Average completion time 20-30 mins.",
    "If you leave and come back it will pick up where you left off.",
  ],
  signature: "",
  buttonLabel: "BEGIN",
  imageUrl: "",
  showImage: true,
});

const PortalSettings = () => {
  const [memberDiscount, setMemberDiscount] = useState(20);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [intro, setIntro] = useState(defaultIntro);
  const [savingAssessmentIntro, setSavingAssessmentIntro] = useState(false);
  const [uploadingIntroImage, setUploadingIntroImage] = useState(false);
  const [labRetailPrice, setLabRetailPrice] = useState(707);
  const [labMemberPrice, setLabMemberPrice] = useState(399);
  const [labDiscountPercent, setLabDiscountPercent] = useState(43);
  const [savingLabPricing, setSavingLabPricing] = useState(false);
  const [freePlanFeatures, setFreePlanFeatures] = useState([]);
  const [freePlanLoading, setFreePlanLoading] = useState(false);
  const [savingFreePlan, setSavingFreePlan] = useState(false);

  useEffect(() => {
    getSiteSettingsApi()
      .then((res) => {
        const data = res?.data || {};
        const pct = Number(data.memberProgramDiscountPercent);
        if (Number.isFinite(pct)) setMemberDiscount(pct);
        if (data.assessmentBeforeYouBegin) {
          const saved = data.assessmentBeforeYouBegin;
          setIntro({
            ...defaultIntro(),
            ...saved,
            bullets:
              Array.isArray(saved.bullets) && saved.bullets.length
                ? saved.bullets
                : [""],
          });
        } else if (typeof data.assessmentBeforeYouBeginEnabled === "boolean") {
          setIntro((prev) => ({
            ...prev,
            enabled: data.assessmentBeforeYouBeginEnabled,
          }));
        }
        const retail = Number(data.labRetailPrice);
        const member = Number(data.labMemberPrice);
        const labPct = Number(data.labDiscountPercent);
        if (Number.isFinite(retail)) setLabRetailPrice(retail);
        if (Number.isFinite(member)) setLabMemberPrice(member);
        if (Number.isFinite(labPct)) setLabDiscountPercent(labPct);
      })
      .catch(() => {});

    setFreePlanLoading(true);
    getFreePlanFeaturesApi()
      .then((data) => {
        setFreePlanFeatures(Array.isArray(data?.features) ? data.features : []);
      })
      .catch(() => {
        setFreePlanFeatures([]);
      })
      .finally(() => setFreePlanLoading(false));
  }, []);

  const updateIntro = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setIntro((prev) => ({ ...prev, [field]: value }));
  };

  const updateBullet = (index) => (event) => {
    const value = event.target.value;
    setIntro((prev) => ({
      ...prev,
      bullets: prev.bullets.map((line, i) => (i === index ? value : line)),
    }));
  };

  const addBullet = () => {
    setIntro((prev) =>
      prev.bullets.length >= 8 ? prev : { ...prev, bullets: [...prev.bullets, ""] }
    );
  };

  const removeBullet = (index) => {
    setIntro((prev) => ({
      ...prev,
      bullets: prev.bullets.filter((_, i) => i !== index),
    }));
  };

  const uploadIntroImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploadingIntroImage(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await axios.post(`${devapi}/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageUrl = res?.data?.data?.fileUrl || res?.data?.data?.filePath || "";
      if (!imageUrl) {
        toast.error("Could not upload the photo.");
        return;
      }
      setIntro((prev) => ({ ...prev, imageUrl, showImage: true }));
      toast.success("Photo uploaded. Save to use it on the pop-up.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not upload the photo.");
    } finally {
      setUploadingIntroImage(false);
    }
  };

  const saveAssessmentIntro = async () => {
    setSavingAssessmentIntro(true);
    try {
      const res = await updateSiteSettingsApi({
        assessmentBeforeYouBegin: {
          enabled: Boolean(intro.enabled),
          title: intro.title || "",
          bullets: intro.bullets || [],
          signature: intro.signature || "",
          buttonLabel: intro.buttonLabel || "BEGIN",
          imageUrl: intro.imageUrl || "",
          showImage: intro.showImage !== false,
        },
      });
      const saved = res?.data?.assessmentBeforeYouBegin;
      if (saved) {
        setIntro({
          ...defaultIntro(),
          ...saved,
          bullets: Array.isArray(saved.bullets) ? saved.bullets : [],
        });
      }
      toast.success("Before You Begin pop-up saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not save the assessment pop-up."
      );
    } finally {
      setSavingAssessmentIntro(false);
    }
  };

  const saveMemberDiscount = async () => {
    const pct = Number(memberDiscount);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Member discount must be between 0 and 100.");
      return;
    }
    setSavingDiscount(true);
    try {
      const res = await updateSiteSettingsApi({
        memberProgramDiscountPercent: pct,
      });
      const saved = Number(res?.data?.memberProgramDiscountPercent);
      if (Number.isFinite(saved)) setMemberDiscount(saved);
      toast.success("Member discount saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save member discount.");
    } finally {
      setSavingDiscount(false);
    }
  };

  const saveLabPricing = async () => {
    const retail = Number(labRetailPrice);
    const member = Number(labMemberPrice);
    const pct = Number(labDiscountPercent);
    if (!Number.isFinite(retail) || retail < 0) {
      toast.error("Lab retail price must be 0 or more.");
      return;
    }
    if (!Number.isFinite(member) || member < 0) {
      toast.error("Lab member price must be 0 or more.");
      return;
    }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Lab discount must be between 0 and 100.");
      return;
    }
    setSavingLabPricing(true);
    try {
      const res = await updateSiteSettingsApi({
        labRetailPrice: retail,
        labMemberPrice: member,
        labDiscountPercent: pct,
      });
      const data = res?.data || {};
      if (Number.isFinite(Number(data.labRetailPrice))) {
        setLabRetailPrice(Number(data.labRetailPrice));
      }
      if (Number.isFinite(Number(data.labMemberPrice))) {
        setLabMemberPrice(Number(data.labMemberPrice));
      }
      if (Number.isFinite(Number(data.labDiscountPercent))) {
        setLabDiscountPercent(Number(data.labDiscountPercent));
      }
      toast.success("Lab panel pricing saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save lab pricing.");
    } finally {
      setSavingLabPricing(false);
    }
  };

  const handleFreePlanFeatureToggle = (key) => (event) => {
    const enabled = event.target.checked;
    setFreePlanFeatures((prev) =>
      prev.map((feature) => (feature.key === key ? { ...feature, enabled } : feature))
    );
  };

  const saveFreePlanFeatures = async () => {
    if (freePlanFeatures.length === 0) {
      toast.error("Free Plan features are not loaded yet.");
      return;
    }
    setSavingFreePlan(true);
    try {
      const res = await updateFreePlanFeaturesApi(
        freePlanFeatures.map((feature) => ({
          key: feature.key,
          enabled: Boolean(feature.enabled),
        }))
      );
      setFreePlanFeatures(
        Array.isArray(res?.features) ? res.features : freePlanFeatures
      );
      toast.success("Free Plan features saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not save Free Plan features."
      );
    } finally {
      setSavingFreePlan(false);
    }
  };

  return (
    <Hoc activeMenu="Portal Settings">
      <div className="program-settings">
        <section className="program-settings__card">
          <header className="program-settings__head">
            <h2>Free Plan portal features</h2>
            <p>
              Same menu options as paid memberships, for users with no active
              subscription. Shop, Community, and Events start off. Community /
              Shop / Events still show a Join pop-up on Free Plan even if
              checked.
            </p>
          </header>
          {freePlanLoading ? (
            <p className="program-settings__loading">Loading Free Plan features…</p>
          ) : (
            <div className="program-settings__features">
              {freePlanFeatures.map((feature) => (
                <label key={feature.key} className="program-settings__feature">
                  <Checkbox
                    checked={Boolean(feature.enabled)}
                    onChange={handleFreePlanFeatureToggle(feature.key)}
                    color="primary"
                    size="small"
                    sx={{ color: "#FEBD36", p: 0.25 }}
                  />
                  <span>
                    <strong>{feature.name}</strong>
                    {feature.description ? <em>{feature.description}</em> : null}
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="program-settings__footer">
            <button
              type="button"
              onClick={saveFreePlanFeatures}
              disabled={
                savingFreePlan || freePlanLoading || freePlanFeatures.length === 0
              }
            >
              {savingFreePlan ? "Saving..." : "Save Free Plan features"}
            </button>
          </div>
        </section>

        <section className="program-settings__card">
          <header className="program-settings__head">
            <h2>Lab panel pricing</h2>
            <p>
              These numbers appear on the Order Labs pop-up: member price,
              crossed-out retail, and the percent-off tag.
            </p>
          </header>
          <div className="program-settings__controls">
            <label className="program-settings__field" htmlFor="lab-retail-price">
              <span>Retail</span>
              <span className="program-settings__input">
                <em>$</em>
                <input
                  id="lab-retail-price"
                  type="number"
                  min="0"
                  step="1"
                  value={labRetailPrice}
                  onChange={(e) => setLabRetailPrice(e.target.value)}
                />
              </span>
            </label>
            <label className="program-settings__field" htmlFor="lab-member-price">
              <span>Member</span>
              <span className="program-settings__input">
                <em>$</em>
                <input
                  id="lab-member-price"
                  type="number"
                  min="0"
                  step="1"
                  value={labMemberPrice}
                  onChange={(e) => setLabMemberPrice(e.target.value)}
                />
              </span>
            </label>
            <label className="program-settings__field" htmlFor="lab-discount-percent">
              <span>Off</span>
              <span className="program-settings__input">
                <input
                  id="lab-discount-percent"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={labDiscountPercent}
                  onChange={(e) => setLabDiscountPercent(e.target.value)}
                />
                <em>%</em>
              </span>
            </label>
            <button
              type="button"
              onClick={saveLabPricing}
              disabled={savingLabPricing}
            >
              {savingLabPricing ? "Saving..." : "Save"}
            </button>
          </div>
        </section>

        <section className="program-settings__card">
          <header className="program-settings__head">
            <h2>Before You Begin pop-up</h2>
            <p>
              Shown once before a new assessment. Edit the title, points,
              signature, button, and photo here. A blank photo keeps the
              built-in founders image.
            </p>
          </header>
          <div className="program-settings__form">
            <div className="program-settings__controls">
              <label className="program-settings__field" htmlFor="assessment-intro">
                <span>{intro.enabled ? "Enabled" : "Disabled"}</span>
              </label>
              <Switch
                id="assessment-intro"
                checked={Boolean(intro.enabled)}
                onChange={updateIntro("enabled")}
                disabled={savingAssessmentIntro}
                color="primary"
              />
            </div>
            <label className="program-settings__field" htmlFor="intro-title">
              <span>Title</span>
              <input
                id="intro-title"
                className="program-settings__text"
                type="text"
                maxLength={200}
                value={intro.title}
                onChange={updateIntro("title")}
              />
            </label>
            <div className="program-settings__field">
              <span>Points</span>
              {intro.bullets.map((line, index) => (
                <div className="program-settings__bullet" key={`intro-bullet-${index}`}>
                  <input
                    className="program-settings__text"
                    type="text"
                    maxLength={500}
                    value={line}
                    onChange={updateBullet(index)}
                    aria-label={`Point ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="program-settings__text-btn"
                    onClick={() => removeBullet(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {intro.bullets.length < 8 && (
                <button
                  type="button"
                  className="program-settings__text-btn"
                  onClick={addBullet}
                >
                  Add point
                </button>
              )}
            </div>
            <label className="program-settings__field" htmlFor="intro-signature">
              <span>Signature</span>
              <input
                id="intro-signature"
                className="program-settings__text"
                type="text"
                maxLength={200}
                value={intro.signature}
                onChange={updateIntro("signature")}
                placeholder="- Sheryl and Layna"
              />
            </label>
            <label className="program-settings__field" htmlFor="intro-button">
              <span>Button label</span>
              <input
                id="intro-button"
                className="program-settings__text"
                type="text"
                maxLength={60}
                value={intro.buttonLabel}
                onChange={updateIntro("buttonLabel")}
              />
            </label>
            <div className="program-settings__controls">
              <label className="program-settings__field" htmlFor="intro-show-image">
                <span>{intro.showImage ? "Photo on" : "Photo off"}</span>
              </label>
              <Switch
                id="intro-show-image"
                checked={intro.showImage !== false}
                onChange={updateIntro("showImage")}
                color="primary"
              />
            </div>
            <label className="program-settings__field" htmlFor="intro-image">
              <span>Photo URL</span>
              <input
                id="intro-image"
                className="program-settings__text"
                type="url"
                maxLength={1000}
                value={intro.imageUrl}
                onChange={updateIntro("imageUrl")}
                placeholder="https:// or upload"
              />
            </label>
            <div className="program-settings__controls">
              <label className="program-settings__upload">
                {uploadingIntroImage ? "Uploading..." : "Upload photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={uploadIntroImage}
                  disabled={uploadingIntroImage}
                />
              </label>
              {intro.imageUrl ? (
                <img
                  className="program-settings__preview"
                  src={intro.imageUrl}
                  alt="Before You Begin preview"
                />
              ) : null}
            </div>
            <div className="program-settings__footer">
              <button
                type="button"
                onClick={saveAssessmentIntro}
                disabled={savingAssessmentIntro || uploadingIntroImage}
              >
                {savingAssessmentIntro ? "Saving..." : "Save pop-up"}
              </button>
            </div>
          </div>
        </section>

        <section className="program-settings__card">
          <header className="program-settings__head">
            <h2>Member discount on programs</h2>
            <p>
              Course cards show the list price and this member price. Members
              are charged the member price at checkout. No coupon is required.
            </p>
          </header>
          <div className="program-settings__controls">
            <label className="program-settings__field" htmlFor="member-program-discount">
              <span>Discount</span>
              <span className="program-settings__input">
                <input
                  id="member-program-discount"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={memberDiscount}
                  onChange={(e) => setMemberDiscount(e.target.value)}
                />
                <em>%</em>
              </span>
            </label>
            <button
              type="button"
              onClick={saveMemberDiscount}
              disabled={savingDiscount}
            >
              {savingDiscount ? "Saving..." : "Save"}
            </button>
          </div>
        </section>
      </div>
    </Hoc>
  );
};

export default PortalSettings;
